/**
 * What was read from the attachments, the price of a line, and the order check.
 *
 * `AttachmentsReadCard` lists every attached file with its document type, number, the
 * lines read from it and, when it was not read, the reason. `LineSourceLink` points a
 * line at the file and page it came from. `LinePriceBlock` prints the list price, the
 * lead time and the margin rule read from IMDP. `OrderCheckPanel` compares a purchase
 * order with the quotation it refers to.
 *
 * Stage 3: a file read from page images says so ("Read from a scan. ..."), with the
 * handwriting hint and the number of lines the two readings did not agree on; a print
 * file without a text layer says "Text recovered from the print file.".
 *
 * Read-only display. Cost, contribution ratio and margin threshold are printed only
 * for a session that may see the margin and only when the server sent them; nothing is
 * computed here.
 */

import { FileText, Icon } from "@norican/icons";
import { Alert, Badge, Card, DataGrid, type DataGridColumnDef, Typography } from "@norican/react";
import { useMemo } from "react";

import {
  type AttachmentRead,
  caseAttachmentChildHref,
  type LineOverride,
  type LineSource,
  type OrderCheck,
  type OrderCheckLine,
} from "@/lib/api";
import { engineFoundText, priceSetByText } from "@/lib/line-price";
import { NOT_APPLYING_TAG } from "@/lib/local-price";
import { type LinePrice } from "@/lib/mock";
import {
  attachmentFileText,
  attachmentNoteLabel,
  docTypeLabel,
  formatAmount,
  formatMoney,
  formatQty,
  formatRatioPercent,
  HANDWRITING_SENTENCE,
  itemStatusTag,
  leadTimeSentence,
  linesDroppedSentence,
  lineSourceText,
  marginTag,
  orderVerdictText,
  orderVerdictTone,
  PHOTO_LINE_TAG,
  priceSentence,
  readMethodSentence,
} from "@/lib/work-labels";

/** The download route of one attachment of a case. */
export function attachmentHref(caseId: string, index: number, child?: number | null): string {
  // Hunt round 2, decision 1: a file inside an attached mail or Office file has its own
  // route under its parent (`getCaseAttachmentChild`).
  return typeof child === "number" && Number.isInteger(child) && child >= 1
    ? caseAttachmentChildHref(caseId, index, child)
    : `/api/cases/${encodeURIComponent(caseId)}/attachments/${index}`;
}

/**
 * Where a read file lives: its own index, or for a file inside another file the index of
 * the parent and its number inside it (hunt round 2).
 */
export function attachmentRef(entry: {
  index: number;
  parent_index?: number | null;
  child?: number | null;
}): { index: number; child: number | null } {
  return entry.parent_index != null && entry.child != null
    ? { index: entry.parent_index, child: entry.child }
    : { index: entry.index, child: null };
}

/** The download link of a read file, a file inside another file included. */
export function attachmentEntryHref(
  caseId: string,
  entry: { index: number; parent_index?: number | null; child?: number | null },
): string {
  const ref = attachmentRef(entry);
  return attachmentHref(caseId, ref.index, ref.child);
}

/**
 * The files the page lists (hunt round 2, decision 1): every file I2Q read or did not
 * read for a reason, but never a logo, a signature or a small picture of the mail body.
 */
export function shownAttachments(attachments: readonly AttachmentRead[]): AttachmentRead[] {
  return attachments.filter(
    (entry) => !(entry.inline && (entry.note === "decoration" || entry.note === "small_image")),
  );
}

/**
 * The preview of a picture or a scanned page, re-encoded by the server from the stored
 * mail: the whole picture, or one part of it (docs/vision-ocr.md §3.5, §6.4).
 */
export function attachmentPreviewHref(
  caseId: string,
  index: number,
  where: {
    page?: number | null;
    part?: number | null;
    size?: "thumb" | "full";
    /** A file inside another file: no preview route, the file itself is shown. */
    child?: number | null;
  } = {},
): string {
  if (where.child != null) {
    return attachmentHref(caseId, index, where.child);
  }
  const query = new URLSearchParams();
  if (typeof where.page === "number" && Number.isInteger(where.page) && where.page >= 1) {
    query.set("page", String(where.page));
  }
  if (typeof where.part === "number" && Number.isInteger(where.part) && where.part >= 1) {
    query.set("part", String(where.part));
  }
  // The server renders `full` (longest side 1024 px) unless asked for a 320 px thumbnail.
  if (where.size === "thumb") {
    query.set("size", "thumb");
  }
  const search = query.toString();
  return `${attachmentHref(caseId, index)}/preview${search ? `?${search}` : ""}`;
}

// ── line source ──────────────────────────────────────────────────────────────

/** "{file}, page {n}" as a link to the file; nothing for a line from the mail text. */
export function LineSourceLink({
  caseId,
  source,
}: {
  caseId: string;
  source: LineSource | null | undefined;
}) {
  const text = lineSourceText(source);
  if (!text || !source || source.kind !== "attachment") {
    return null;
  }
  return (
    <a
      href={attachmentHref(caseId, source.index, source.child)}
      download={source.file}
      title={`Open ${source.file}`}
      className="text-accent-fg-subtle mt-0.5 block pl-2 text-xs break-all hover:underline"
    >
      {text}
    </a>
  );
}

// ── attachments read ─────────────────────────────────────────────────────────

function linesReadText(count: number): string {
  return `${count} ${count === 1 ? "line" : "lines"} read`;
}

export function AttachmentsReadCard({
  caseId,
  attachments,
  className,
}: {
  caseId: string;
  attachments: AttachmentRead[];
  className?: string;
}) {
  // A logo or a signature of the mail body is never listed (hunt round 2, decision 1).
  const shown = shownAttachments(attachments);
  if (shown.length === 0) {
    return null;
  }
  return (
    <Card variant="outline" padding="none" className={className}>
      <Card.Header padding="sm" className="border-border-subtle flex items-center gap-2 border-b">
        <Typography variant="label">Attachments read</Typography>
        <Typography variant="caption" tone="subtle" className="ml-auto">
          {shown.length} {shown.length === 1 ? "file" : "files"}
        </Typography>
      </Card.Header>
      <Card.Body>
        <ul className="flex flex-col gap-2.5">
          {shown.map((attachment) => {
            const href = attachmentEntryHref(caseId, attachment);
            const note = attachmentNoteLabel(attachment.note);
            const docType = docTypeLabel(attachment.doc_type);
            const count = attachment.lines_read ?? 0;
            const fromScan = attachment.method === "image";
            // A loose picture says "Read from a photo" in its own panel, not "Read from a scan".
            const method =
              fromScan && attachment.kind === "image"
                ? PHOTO_LINE_TAG
                : readMethodSentence(attachment.method);
            const dropped = linesDroppedSentence(attachment.lines_dropped);
            const facts = [
              docType,
              attachment.doc_number ? `No. ${attachment.doc_number}` : null,
              count > 0 || !note ? linesReadText(count) : null,
            ].filter(Boolean);
            return (
              <li
                key={`${attachment.index}:${attachment.parent_index ?? ""}:${attachment.child ?? ""}`}
                className="flex min-w-0 flex-col gap-0.5"
                data-attachment-read={
                  attachment.inline ? "inline" : attachment.child ? "child" : "file"
                }
              >
                <a
                  href={href}
                  download={attachment.file}
                  className="text-accent-fg-subtle inline-flex min-w-0 items-center gap-1.5 text-sm font-semibold hover:underline"
                >
                  <Icon icon={FileText} size="xs" />
                  <span className="break-all">{attachmentFileText(attachment)}</span>
                </a>
                {facts.length > 0 && (
                  <span className="text-fg-muted text-xs break-words">{facts.join(", ")}</span>
                )}
                {note && <span className="text-warning-fg text-xs break-words">{note}</span>}
                {method && (
                  <span
                    className={
                      fromScan
                        ? "text-warning-fg text-xs break-words"
                        : "text-fg-muted text-xs break-words"
                    }
                  >
                    {method}
                  </span>
                )}
                {fromScan && attachment.handwriting === true && (
                  <span className="text-warning-fg text-xs break-words">
                    {HANDWRITING_SENTENCE}
                  </span>
                )}
                {dropped && <span className="text-warning-fg text-xs break-words">{dropped}</span>}
                {fromScan && (
                  <a
                    href={href}
                    download={attachment.file}
                    className="text-accent-fg-subtle w-fit text-xs font-semibold hover:underline"
                  >
                    Open the file to check
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      </Card.Body>
    </Card>
  );
}

// ── price and lead time ──────────────────────────────────────────────────────

/**
 * The price line of one draft line: list price, lead time, margin and item status.
 * Renders nothing without a price status or for a line without an item. A price a
 * person set (round 4) says who set it and when, what I2Q found, and the note.
 */
export function LinePriceBlock({
  price,
  showCost = false,
  customerSuggested = false,
  note = null,
  override = null,
}: {
  price: LinePrice | null | undefined;
  /** True when the session may see the margin: cost figures are printed when the server sent them. */
  showCost?: boolean;
  /** True when a customer is suggested but not confirmed yet. */
  customerSuggested?: boolean;
  /**
   * sim360 P33 (`localPriceNote`): the local price is no price to quote (greyed out and
   * tagged), or a hint whom to ask for the price of an item of another catalogue.
   */
  note?: { greyed: boolean; text: string } | null;
  /** The line's override: behind a manual price it holds what I2Q found and the note. */
  override?: Pick<LineOverride, "engine_unit_price" | "engine_currency" | "price_note"> | null;
}) {
  if (!price) {
    return null;
  }
  const sentence = priceSentence({ ...price, customerSuggested });
  if (!sentence) {
    return null;
  }
  const greyed = Boolean(note?.greyed);
  const found = price.status === "found";
  const manual = found && !greyed && price.source === "sales";
  const tag = found && !greyed ? marginTag(price.margin) : null;
  // A margin note says why the check did not run (the cost is per another unit, a price
  // set by sales in another currency or without a cost): the badge says "not checked"
  // and the note itself is printed under it. A fixed "(units differ)" read wrong on a
  // hand-set price (tester round 5, 12 Oct 2026).
  const margin = tag && price.marginNote ? { ...tag, label: "Margin: not checked" } : tag;
  const itemStatus = itemStatusTag(price.itemStatus, price.replacement);
  const figures = showCost
    ? [
        price.unitCost ? `Cost ${formatMoney(price.unitCost, price.currency)}` : null,
        price.contributionRatio
          ? `Contribution ratio ${formatRatioPercent(price.contributionRatio)}`
          : null,
        price.marginThreshold ? `Threshold ${formatRatioPercent(price.marginThreshold)}` : null,
      ].filter(Boolean)
    : [];
  return (
    <span
      className="mt-1 flex flex-col gap-0.5 text-xs"
      data-price-applies={greyed ? "no" : undefined}
    >
      {greyed ? (
        <span className="flex flex-wrap items-center gap-1">
          <span className="text-fg-subtle break-words line-through">{sentence}</span>
          <Badge tone="neutral" size="xs" className="h-auto text-left whitespace-normal">
            {NOT_APPLYING_TAG}
          </Badge>
        </span>
      ) : (
        <span className={found ? "text-fg break-words" : "text-fg-muted break-words"}>
          {sentence}
        </span>
      )}
      {manual && (
        <>
          <span className="text-fg break-words" data-price-source="sales">
            {priceSetByText(price)}
          </span>
          <span className="text-fg-muted break-words">
            {engineFoundText(override?.engine_unit_price, override?.engine_currency)}
          </span>
          {override?.price_note && (
            <span className="text-fg-muted break-words">Note: {override.price_note}</span>
          )}
        </>
      )}
      {note && <span className="text-fg-muted break-words">{note.text}</span>}
      {price.status === "other_entity" && price.referenceText && (
        <span className="text-fg-muted break-words" data-reference-price>
          {price.referenceText}
        </span>
      )}
      {(found || price.leadDays != null || price.leadStatus === "none_in_fo") && (
        <span className="text-fg-muted">
          {leadTimeSentence(price.leadDays, price.leadSource, price.leadStatus)}
        </span>
      )}
      {(margin || itemStatus) && (
        <span className="flex flex-wrap gap-1">
          {margin && (
            <Badge tone={margin.tone} size="xs" className="h-auto text-left whitespace-normal">
              {margin.label}
            </Badge>
          )}
          {margin && price.marginNote && (
            <span className="text-fg-muted break-words" data-margin-note>
              {price.marginNote}
            </span>
          )}
          {itemStatus && (
            <Badge tone={itemStatus.tone} size="xs" className="h-auto text-left whitespace-normal">
              {itemStatus.label}
            </Badge>
          )}
        </span>
      )}
      {margin &&
        price.margin === "needs_approval" &&
        (price.marginBandText || price.marginApprover) && (
          <span className="text-fg-muted break-words" data-margin-band>
            {[price.marginBandText, price.marginApprover].filter(Boolean).join(". ")}
          </span>
        )}
      {margin && price.priceBasisText && (
        <span className="text-fg-muted break-words">{price.priceBasisText}.</span>
      )}
      {found && price.marginNote && (
        <span className="text-fg-muted break-words" data-margin-note="">
          {price.marginNote}
        </span>
      )}
      {!found && price.previousUnitPrice && (
        <span className="text-fg-subtle break-words" data-previous-price="">
          Earlier price {formatMoney(price.previousUnitPrice, price.previousCurrency ?? null)} is
          not valid today and is not in the order value.
        </span>
      )}
      {figures.length > 0 && (
        <span className="text-fg-subtle break-words">Managers only: {figures.join(", ")}</span>
      )}
    </span>
  );
}

// ── order check ──────────────────────────────────────────────────────────────

function QtyPrice({ qty, price }: { qty: number | null; price: number | null }) {
  if (qty == null && price == null) {
    return <span className="text-fg-subtle text-xs">-</span>;
  }
  return (
    <span className="block text-xs tabular-nums">
      {qty != null && <span className="text-fg block">{formatQty(qty)}</span>}
      {price != null && <span className="text-fg-muted block">at {formatAmount(price)}</span>}
    </span>
  );
}

/** The purchase order against its quotation. Sits above the lines; the lines stay visible. */
export function OrderCheckPanel({ check }: { check: OrderCheck }) {
  const columns = useMemo<DataGridColumnDef<OrderCheckLine>[]>(
    () => [
      {
        id: "line",
        header: "Order line",
        cell: (row) => (
          <span className="block min-w-0 text-xs">
            <span className="text-fg-subtle">Line {row.line_index + 1}</span>
            <span className="text-fg block font-mono font-bold break-all">
              {row.item ?? "no item"}
            </span>
          </span>
        ),
      },
      {
        id: "ordered",
        header: "Ordered",
        width: 104,
        align: "end",
        cell: (row) => <QtyPrice qty={row.ordered_qty} price={row.ordered_price} />,
      },
      {
        id: "quoted",
        header: "Quoted",
        width: 104,
        align: "end",
        cell: (row) => <QtyPrice qty={row.quoted_qty} price={row.quoted_price} />,
      },
      {
        id: "verdict",
        header: "Check",
        width: 200,
        cell: (row) => (
          <Badge
            tone={orderVerdictTone(row.verdict)}
            size="sm"
            className="h-auto w-fit text-left whitespace-normal"
          >
            {orderVerdictText(row)}
          </Badge>
        ),
      },
    ],
    [],
  );

  return (
    <Card variant="outline" padding="none" className="overflow-hidden">
      <Card.Header padding="sm" className="border-border-subtle flex items-center gap-2 border-b">
        <Typography variant="label">Order check</Typography>
        {check.quotation && (
          <Typography variant="caption" tone="subtle" className="ml-auto font-mono">
            {check.quotation}
          </Typography>
        )}
      </Card.Header>
      <Card.Body padding="none">
        <div className="flex flex-col gap-2 px-4 py-3">
          {check.coverage_text && (
            <Typography variant="body-sm" className="text-fg break-words">
              {check.coverage_text}
            </Typography>
          )}
          {check.header_flags.map((flag) => (
            <Alert key={flag.code} tone={flag.severity === "warning" ? "warning" : "info"}>
              {flag.text}
            </Alert>
          ))}
        </div>
        {check.lines.length > 0 && (
          <div className="overflow-x-auto">
            <DataGrid<OrderCheckLine>
              columns={columns}
              data={check.lines}
              getRowId={(row) => String(row.line_index)}
              density="compact"
              tableLayout="fixed"
              className="min-w-[520px]"
              aria-label="Order lines compared with the quotation"
            />
          </div>
        )}
        {check.not_ordered.length > 0 && (
          <div className="border-border-subtle border-t px-4 py-3">
            <Typography variant="caption" tone="subtle">
              Quoted but not ordered
            </Typography>
            <ul className="mt-1 flex flex-col gap-1">
              {check.not_ordered.map((line, index) => (
                <li key={`${line.item}-${index}`} className="text-fg text-sm break-words">
                  <span className="font-mono font-bold">{line.item}</span>
                  {line.description ? ` ${line.description}` : ""}
                  {line.qty != null ? `, quantity ${formatQty(line.qty)}` : ""}
                  {line.unit_price != null ? `, at ${formatAmount(line.unit_price)}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
